import {
  Check as CheckIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { RoutalInputText, RoutalInputTextProps, RoutalPalette, RoutalText } from '@nx-smartmonkey/ui';
import { useState } from 'react';
import { useIntl } from 'react-intl';

export const validatePassword = (pass?: string, email?: string) => {
  if (pass === undefined)
    return {
      isValid: false,
      details: {
        minLength: false,
        hasSpecialCharacter: false,
        hasUpperCase: false,
        hasLowerCase: false,
        notMatchesEmail: false,
      },
    };

  const minLength = pass.length >= 8;
  const hasSpecialCharacter = /[!@#$%^&*(),.?":{}|<>_-]/.test(pass);
  const hasUpperCase = /[A-Z]/.test(pass);
  const hasLowerCase = /[a-z]/.test(pass);
  const notMatchesEmail = (email ?? ``) !== `` ? pass !== email!.split(`@`)[0] : true;

  return {
    isValid: minLength && hasSpecialCharacter && hasUpperCase && hasLowerCase && notMatchesEmail,
    details: { minLength, hasSpecialCharacter, hasUpperCase, hasLowerCase, notMatchesEmail },
  };
};

export interface InputPasswordProps extends RoutalInputTextProps {
  placeholderIntlId?: string;
  placeholderIntlValues?: Record<string, string | number>;
  onChange?: (value: string) => void;
  email?: string;
  withEye?: boolean;
  showHints?: boolean;
}

export const PasswordInput = ({
  placeholderIntlId,
  placeholderIntlValues,
  onChange,
  email,
  withEye,
  showHints = true,
  ...inputTextProps
}: InputPasswordProps) => {
  const [passwordVisible, setPasswordVisible] = useState<boolean>(false);

  const [validation, setValidation] = useState<{
    isValid: boolean;
    details: {
      minLength: boolean;
      hasSpecialCharacter: boolean;
      hasUpperCase: boolean;
      hasLowerCase: boolean;
      notMatchesEmail: boolean;
    };
  }>({
    isValid: false,
    details: {
      minLength: false,
      hasSpecialCharacter: false,
      hasUpperCase: false,
      hasLowerCase: false,
      notMatchesEmail: false,
    },
  });
  const [password, setPassword] = useState<string>(``);

  const intl = useIntl();
  let processedPlaceholder: string;

  // Select the content of the placeholder
  if (placeholderIntlId) {
    processedPlaceholder = intl.formatMessage({ id: placeholderIntlId }, placeholderIntlValues);
  } else {
    processedPlaceholder = inputTextProps.placeholder ?? inputTextProps.suffix ?? intl.formatMessage({ id: `none` });
  }

  return (
    <>
      {
        <>
          <RoutalInputText
            {...inputTextProps}
            {...(withEye ? { type: passwordVisible ? `text` : `password` } : {})}
            onKeyDown={(e: any) => {
              inputTextProps.onKeyDown && inputTextProps.onKeyDown(e);
            }}
            onChange={(psw: any) => {
              setPassword(psw);
              if (onChange) {
                onChange(psw);
              }
              setValidation(validatePassword(psw, email));
            }}
            onFocus={() => {
              inputTextProps.onFocus && inputTextProps.onFocus();
            }}
            onBlur={() => {
              inputTextProps.onBlur && inputTextProps.onBlur();
            }}
            placeholder={processedPlaceholder}
            inputContainerStyles={{
              width: `100%`,
              border:
                !showHints || password === `` || validatePassword(password, email).isValid
                  ? undefined
                  : `1px solid ${RoutalPalette.error.medium}`,
              ...inputTextProps.inputContainerStyles,
            }}
            endAdornment={
              <>
                {inputTextProps.endAdornment}
                {withEye ? (
                  <div
                    style={{
                      display: `flex`,
                      alignItems: `center`,
                      justifyContent: `center`,
                      cursor: `pointer`,
                      color: RoutalPalette.primary00,
                      marginRight: `5px`,
                    }}
                    onClick={() => {
                      setPasswordVisible(!passwordVisible);
                    }}
                  >
                    {passwordVisible ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </div>
                ) : (
                  <>
                    {showHints && password !== `` && validatePassword(password, email).isValid && (
                      <CheckIcon style={{ color: RoutalPalette.success.medium, marginRight: `5px` }} />
                    )}
                  </>
                )}
              </>
            }
          />

          {showHints && password !== `` && (
            <>
              {!validatePassword(password, email).isValid ? (
                <div
                  style={{
                    marginTop: `8px`,
                    padding: `14px`,

                    backgroundColor: RoutalPalette.neutral05,
                    borderRadius: `4px`,
                  }}
                >
                  <RoutalText style={{ color: RoutalPalette.secondary40, marginBottom: `8px` }}>
                    {intl.formatMessage({ id: `password.errorhint.must_contain` })}
                  </RoutalText>
                  <ul style={{ marginBottom: `0px` }}>
                    <li>
                      <RoutalText
                        style={{
                          color: validation.details.minLength
                            ? RoutalPalette.success.medium
                            : RoutalPalette.secondary40,
                        }}
                      >
                        {intl.formatMessage({ id: `password.errorhint.min_lenght` })}
                      </RoutalText>
                    </li>
                    <li>
                      <RoutalText
                        style={{
                          color: validation.details.hasSpecialCharacter
                            ? RoutalPalette.success.medium
                            : RoutalPalette.secondary40,
                        }}
                      >
                        {intl.formatMessage({ id: `password.errorhint.special_character` })}
                      </RoutalText>
                    </li>
                    <li>
                      <RoutalText
                        style={{
                          color:
                            validation.details.hasLowerCase && validation.details.hasUpperCase
                              ? RoutalPalette.success.medium
                              : RoutalPalette.secondary40,
                        }}
                      >
                        {intl.formatMessage({ id: `password.errorhint.low_upper` })}
                      </RoutalText>
                    </li>
                    {!validation.details.notMatchesEmail && (
                      <li>
                        <RoutalText style={{ color: RoutalPalette.secondary40 }}>
                          {intl.formatMessage({ id: `password.errorhint.email` })}
                        </RoutalText>
                      </li>
                    )}
                  </ul>
                </div>
              ) : (
                <>
                  {withEye && (
                    <RoutalText style={{ color: RoutalPalette.success.medium, marginTop: `8px` }}>
                      {intl.formatMessage({ id: `password.validhint` })}
                    </RoutalText>
                  )}
                </>
              )}
            </>
          )}
        </>
      }
    </>
  );
};
